import {Button} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  ActivityLine,
  ClockLine,
  CommentLine,
  CopyLine,
  DownloadLine,
  DropboxPaperLine,
  MetricsLine,
  OpenLine,
  PersonMultipleLine,
  SyncingLine,
} from '@dropbox/dig-icons/assets';
import {useQuery} from '@tanstack/react-query';
import {OpenAPI} from 'client/core/OpenAPI';
import {request as __request} from 'client/core/request';
import {Avatar} from 'components/DSYS/Avatar';
import {Link} from 'components/DSYS/Link';
import type {ComponentType, ReactNode, SVGProps} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {getBackendUrl} from 'utilities';

import styles from './AdminPlanningLive.module.css';

type LivePlanningCollaborator = {
  user_id: string;
  name: string;
  connections: number;
  connection_state_counts?: {
    active: number;
    idle: number;
    typing: number;
    commenting: number;
  };
  last_seen?: number | null;
  state: string;
  state_updated_at?: number | null;
  broadcasts_per_minute: number;
  yjs_updates_per_minute: number;
  awareness_updates_per_minute: number;
  stateless_broadcasts_per_minute: number;
};

type LivePlanningDocument = {
  planning_id: string;
  editor_doc_db_id: number;
  document_id: string;
  document_name: string;
  title: string;
  owner_name?: string | null;
  owner_ldap?: string | null;
  planning_url: string;
  rollback_url: string;
  export_url: string;
  connected_clients: number;
  collaborators: LivePlanningCollaborator[];
  yjs_size_bytes?: number | null;
  live_yjs_size_bytes?: number | null;
  memory_estimate_bytes?: number | null;
  broadcasts_per_minute: number;
  yjs_updates_per_minute: number;
  awareness_updates_per_minute: number;
  stateless_broadcasts_per_minute: number;
  loaded_at?: number | null;
  comment_counts: {
    total_threads: number;
    open_threads: number;
    resolved_threads: number;
    total_comments: number;
    unattached_comments: number;
  };
  debug_ids: {
    editor_doc_db_id: number;
    encrypted_editor_doc_id: string;
    document_id: string;
    owner_id?: string | null;
    tenant_id?: string | null;
    sql: string[];
  };
};

type LivePlanningAdminResponse = {
  generated_at: string;
  documents: LivePlanningDocument[];
  server_load: {
    status: string;
    active_connections: number;
    connected_user_count: number;
    active_documents: number;
    heap_used_mb?: number | null;
    heap_total_mb?: number | null;
    rss_mb?: number | null;
    websocket_tracking_enabled?: boolean | null;
    websocket_messages_total?: number | null;
    websocket_messages_per_second?: number | null;
    websocket_bytes_total?: number | null;
    websocket_bytes_per_second?: number | null;
    store_avg_time_ms?: number | null;
    store_max_time_ms?: number | null;
    fetch_avg_time_ms?: number | null;
    fetch_max_time_ms?: number | null;
    auth_failures_recent: number;
    uptime_formatted?: string | null;
  };
  collab_error?: string | null;
};

type IconSrc = ComponentType<SVGProps<SVGSVGElement>>;
type LiveTableDensity = 'full' | 'balanced' | 'compact';

const FULL_TABLE_MIN_WIDTH = 1120;
const BALANCED_TABLE_MIN_WIDTH = 760;

const collaboratorLimitByDensity: Record<LiveTableDensity, number> = {
  full: 20,
  balanced: 20,
  compact: 20,
};

const getLiveTableDensity = (width: number | null): LiveTableDensity => {
  if (width == null || width >= FULL_TABLE_MIN_WIDTH) return 'full';
  if (width >= BALANCED_TABLE_MIN_WIDTH) return 'balanced';
  return 'compact';
};

const useMeasuredElementWidth = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateWidth = () => setWidth(element.getBoundingClientRect().width);
    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      setWidth(entry?.contentRect.width ?? element.getBoundingClientRect().width);
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return [ref, width] as const;
};

const setOpenApiBackend = () => {
  OpenAPI.BASE = getBackendUrl();
  OpenAPI.CREDENTIALS = 'include';
  OpenAPI.WITH_CREDENTIALS = true;
};

const fetchLivePlanningAdmin = async (): Promise<LivePlanningAdminResponse> => {
  setOpenApiBackend();
  return __request(OpenAPI, {
    method: 'GET',
    url: '/api/v1/editordoc/live-admin',
  }) as Promise<LivePlanningAdminResponse>;
};

const formatBytes = (bytes?: number | null): string => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatNumber = (value?: number | null, digits = 1): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString(undefined, {
    maximumFractionDigits: digits,
  });
};

const formatConnectionCount = (connections: number): string =>
  `${formatNumber(connections, 0)} ${connections === 1 ? 'connection' : 'connections'}`;

const formatConnectionStateCounts = (
  counts?: LivePlanningCollaborator['connection_state_counts']
): string | null => {
  if (!counts) return null;

  const stateCounts: Array<[string, number]> = [
    ['typing', counts.typing],
    ['commenting', counts.commenting],
    ['active', counts.active],
    ['idle', counts.idle],
  ];
  const parts = stateCounts
    .filter(([, count]) => count > 0)
    .map(([state, count]) => `${count} ${state}`);

  return parts.length ? parts.join(', ') : null;
};

const formatMs = (value?: number | null): string => {
  if (value == null) return '—';
  return `${formatNumber(value, 0)} ms`;
};

const formatTimeAgo = (timestampMs?: number | null): string => {
  if (!timestampMs) return '—';
  const seconds = Math.max(0, Math.floor((Date.now() - timestampMs) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`;
};

const formatStateLabel = (state: string) =>
  state
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const stateTooltip = (collaborator: LivePlanningCollaborator) => {
  const stateLabel = formatStateLabel(collaborator.state);
  const activityTime = formatTimeAgo(collaborator.last_seen);
  const activityText = activityTime !== '—' ? ` was seen ${activityTime}` : '';
  return (
    `${stateLabel}: ${collaborator.name}${activityText}. ` +
    `${formatNumber(collaborator.broadcasts_per_minute, 0)} client-originated broadcasts/min ` +
    `(${formatNumber(collaborator.yjs_updates_per_minute, 0)} yjs, ` +
    `${formatNumber(collaborator.awareness_updates_per_minute, 0)} awareness).`
  );
};

const connectionTooltip = (collaborator: LivePlanningCollaborator) => {
  const stateSummary = formatConnectionStateCounts(collaborator.connection_state_counts);
  return `${collaborator.name} has ${formatConnectionCount(
    collaborator.connections
  )} open on this document${stateSummary ? `: ${stateSummary}` : ''}.`;
};

const collaboratorAvatarUser = (collaborator: LivePlanningCollaborator) => ({
  name: collaborator.name,
  profile_photo_url: `/api/v1/images/${collaborator.user_id}.jpg`,
});

const stateClass = (state: string) => {
  switch (state) {
    case 'active':
      return styles.stateActive;
    case 'typing':
      return styles.stateTyping;
    case 'commenting':
      return styles.stateCommenting;
    case 'idle':
      return styles.stateIdle;
    default:
      return styles.stateUnknown;
  }
};

const sqlForDocument = (doc: LivePlanningDocument): string =>
  doc.debug_ids.sql.join('\n') +
  `\n\n-- IDs\n-- editor_doc_id=${doc.editor_doc_db_id}\n-- planning_id=${doc.planning_id}\n-- document_id=${doc.document_id}`;

const MetricPanel = ({
  label,
  value,
  subtext,
  icon,
}: {
  label: string;
  value: string;
  subtext?: string;
  icon: IconSrc;
}) => (
  <div className={styles.summaryPanel}>
    <div className={styles.summaryHeader}>
      <span className={styles.summaryIcon}>
        <UIIcon src={icon} size="small" />
      </span>
      <Text size="small" color="subtle">
        {label}
      </Text>
    </div>
    <div className={styles.summaryValue}>{value}</div>
    {subtext ? <div className={styles.summarySubtext}>{subtext}</div> : null}
  </div>
);

const collaboratorStatePriority: Record<string, number> = {
  typing: 0,
  commenting: 1,
  active: 2,
  idle: 3,
};

const getSortedCollaborators = (collaborators: LivePlanningCollaborator[]) =>
  [...collaborators].sort((left, right) => {
    const priorityDelta =
      (collaboratorStatePriority[left.state] ?? 10) -
      (collaboratorStatePriority[right.state] ?? 10);
    if (priorityDelta !== 0) return priorityDelta;
    return (right.last_seen ?? 0) - (left.last_seen ?? 0);
  });

const CollaboratorSummary = ({collaborators}: {collaborators: LivePlanningCollaborator[]}) => {
  const activeCount = collaborators.filter((collaborator) =>
    ['active', 'typing', 'commenting'].includes(collaborator.state)
  ).length;
  const idleCount = collaborators.filter((collaborator) => collaborator.state === 'idle').length;

  return (
    <div className={styles.collaboratorSummary}>
      <span className={styles.stateSummaryPill}>
        <span className={`${styles.compactStateDot} ${styles.stateActive}`} />
        {activeCount} active
      </span>
      <span className={styles.stateSummaryPill}>
        <span className={`${styles.compactStateDot} ${styles.stateIdle}`} />
        {idleCount} idle
      </span>
    </div>
  );
};

const CollaboratorsCell = ({
  collaborators,
  density,
}: {
  collaborators: LivePlanningCollaborator[];
  density: LiveTableDensity;
}) => {
  if (!collaborators.length) {
    return (
      <Text size="small" color="faint">
        —
      </Text>
    );
  }

  const visibleLimit = collaboratorLimitByDensity[density];
  const sortedCollaborators = getSortedCollaborators(collaborators);
  const visibleCollaborators = sortedCollaborators.slice(0, visibleLimit);
  const overflowCollaborators = sortedCollaborators.slice(visibleLimit);
  const overflowTooltip = overflowCollaborators
    .map((collaborator) => `${collaborator.name} (${formatStateLabel(collaborator.state)})`)
    .join(', ');

  return (
    <div className={styles.collaboratorsCell} data-density={density}>
      <CollaboratorSummary collaborators={collaborators} />
      <div className={styles.collaborators} data-count={visibleCollaborators.length}>
        {visibleCollaborators.map((collaborator) => (
          <div className={styles.collaborator} key={collaborator.user_id}>
            <div className={styles.avatarWrap}>
              <Avatar user={collaboratorAvatarUser(collaborator)} size="small" />
            </div>
            <Box className={styles.collaboratorIdentity}>
              <Text size="small" isBold className={styles.collaboratorName}>
                {collaborator.name}
              </Text>
              <Tooltip title={stateTooltip(collaborator)} placement="right-start">
                <span className={styles.collaboratorStateLine}>
                  <span
                    className={`${styles.compactStateDot} ${stateClass(collaborator.state)}`}
                    aria-label={formatStateLabel(collaborator.state)}
                  />
                  <span>{formatStateLabel(collaborator.state)}</span>
                  <span className={styles.collaboratorRate}>
                    · {formatNumber(collaborator.broadcasts_per_minute, 0)}/min
                  </span>
                  {collaborator.connections > 1 ? (
                    <Tooltip title={connectionTooltip(collaborator)} placement="top">
                      <span className={styles.collaboratorConnections}>
                        · {formatConnectionCount(collaborator.connections)}
                      </span>
                    </Tooltip>
                  ) : null}
                </span>
              </Tooltip>
            </Box>
          </div>
        ))}
        {overflowCollaborators.length > 0 ? (
          <Tooltip title={overflowTooltip} placement="right-start">
            <span className={styles.moreCollaborators}>+{overflowCollaborators.length} more</span>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
};

const IdCopyButton = ({
  copyKey,
  copiedIdKey,
  label,
  value,
  onCopyId,
}: {
  copyKey: string;
  copiedIdKey: string | null;
  label: string;
  value: string | number;
  onCopyId: (value: string | number, key: string) => void;
}) => (
  <Tooltip title={copiedIdKey === copyKey ? 'Copied' : `Copy ${label}`} placement="top">
    <button
      type="button"
      className={styles.idCopyButton}
      aria-label={`Copy ${label}`}
      onClick={() => onCopyId(value, copyKey)}
    >
      <UIIcon src={CopyLine} size="small" />
    </button>
  </Tooltip>
);

const DocumentIdentity = ({
  doc,
  density,
  copiedIdKey,
  onCopyId,
}: {
  doc: LivePlanningDocument;
  density: LiveTableDensity;
  copiedIdKey: string | null;
  onCopyId: (value: string | number, key: string) => void;
}) => (
  <div className={styles.docTitle} data-density={density}>
    <Link to={doc.planning_url} hasNoUnderline showUnderlineOnHover>
      <Text isBold>{doc.title}</Text>
    </Link>
    <div className={styles.docMetaLine}>
      <Text size="small" color="faint">
        {doc.owner_name ?? 'No owner'}
      </Text>
      <Text size="small" color="faint">
        Loaded {formatTimeAgo(doc.loaded_at)}
      </Text>
    </div>
    <div className={styles.docIds}>
      <div className={styles.docIdRow}>
        <span className={styles.docIdLabel}>Editor</span>
        <Text size="small" className={styles.mono}>
          {doc.editor_doc_db_id}
        </Text>
        <IdCopyButton
          copyKey={`${doc.document_id}:editor`}
          copiedIdKey={copiedIdKey}
          label="editor doc ID"
          value={doc.editor_doc_db_id}
          onCopyId={onCopyId}
        />
      </div>
      <div className={styles.docIdRow}>
        <span className={styles.docIdLabel}>Document</span>
        <Text size="small" className={styles.mono}>
          {doc.document_id}
        </Text>
        <IdCopyButton
          copyKey={`${doc.document_id}:document`}
          copiedIdKey={copiedIdKey}
          label="document ID"
          value={doc.document_id}
          onCopyId={onCopyId}
        />
      </div>
      <div className={styles.docIdRow}>
        <span className={styles.docIdLabel}>Public ID</span>
        <Text size="small" className={styles.mono}>
          {doc.planning_id}
        </Text>
        <IdCopyButton
          copyKey={`${doc.document_id}:planning`}
          copiedIdKey={copiedIdKey}
          label="public planning ID"
          value={doc.planning_id}
          onCopyId={onCopyId}
        />
      </div>
    </div>
  </div>
);

const MetadataBlock = ({doc}: {doc: LivePlanningDocument}) => (
  <div className={styles.metricStack}>
    <Chip size="small" className={styles.clientChip}>
      <Chip.IconAccessory>
        <UIIcon src={PersonMultipleLine} size="small" />
      </Chip.IconAccessory>
      <Chip.Content>{formatConnectionCount(doc.connected_clients)}</Chip.Content>
    </Chip>
    <Text size="small">Yjs {formatBytes(doc.live_yjs_size_bytes ?? doc.yjs_size_bytes)}</Text>
    <Text size="small" color="faint">
      Memory est. {formatBytes(doc.memory_estimate_bytes)}
    </Text>
  </div>
);

const CommentsBlock = ({doc}: {doc: LivePlanningDocument}) => (
  <div className={styles.metricStack}>
    <Text size="small" isBold>
      {doc.comment_counts.total_comments} comments
    </Text>
    <Text size="small" color="faint">
      {doc.comment_counts.open_threads} open threads
    </Text>
    <Text size="small" color="faint">
      {doc.comment_counts.resolved_threads} resolved
    </Text>
  </div>
);

const BroadcastsBlock = ({
  doc,
  maxBroadcastsPerMinute,
}: {
  doc: LivePlanningDocument;
  maxBroadcastsPerMinute: number;
}) => (
  <div className={styles.metricStack}>
    <div className={styles.broadcastHeader}>
      <Text size="small" isBold>
        {doc.broadcasts_per_minute}
      </Text>
      <Text size="small" color="faint">
        /min
      </Text>
    </div>
    <div className={styles.broadcastMeter} aria-hidden="true">
      <span
        style={{
          width: `${Math.max(6, (doc.broadcasts_per_minute / maxBroadcastsPerMinute) * 100)}%`,
        }}
      />
    </div>
    <Text size="small" color="faint">
      {doc.yjs_updates_per_minute} yjs
    </Text>
    <Text size="small" color="faint">
      {doc.awareness_updates_per_minute} awareness
    </Text>
    <Text size="small" color="faint">
      {doc.stateless_broadcasts_per_minute} stateless
    </Text>
  </div>
);

const ActivityGroup = ({label, children}: {label: string; children: ReactNode}) => (
  <div className={styles.activityGroup}>
    <div className={styles.activityLabel}>{label}</div>
    {children}
  </div>
);

const ActivityCell = ({
  doc,
  maxBroadcastsPerMinute,
}: {
  doc: LivePlanningDocument;
  maxBroadcastsPerMinute: number;
}) => (
  <div className={styles.activityGrid}>
    <ActivityGroup label="Live metadata">
      <MetadataBlock doc={doc} />
    </ActivityGroup>
    <ActivityGroup label="Comments">
      <CommentsBlock doc={doc} />
    </ActivityGroup>
    <ActivityGroup label="Broadcasts">
      <BroadcastsBlock doc={doc} maxBroadcastsPerMinute={maxBroadcastsPerMinute} />
    </ActivityGroup>
  </div>
);

const DocumentActions = ({
  doc,
  copiedDocumentId,
  onOpen,
  onRollback,
  onExport,
  onCopySql,
  density,
}: {
  doc: LivePlanningDocument;
  copiedDocumentId: string | null;
  onOpen: (doc: LivePlanningDocument) => void;
  onRollback: (doc: LivePlanningDocument) => void;
  onExport: (doc: LivePlanningDocument) => void;
  onCopySql: (doc: LivePlanningDocument) => void;
  density: LiveTableDensity;
}) => (
  <div
    className={`${styles.actions} ${density === 'compact' ? styles.actionsCompact : ''}`}
    data-density={density}
  >
    <Button
      size="small"
      variant="outline"
      onClick={() => onOpen(doc)}
      withIconStart={<UIIcon src={OpenLine} />}
    >
      Open
    </Button>
    <Button
      size="small"
      variant="outline"
      onClick={() => onRollback(doc)}
      withIconStart={<UIIcon src={ClockLine} />}
    >
      Rollback
    </Button>
    <Button
      size="small"
      variant="outline"
      onClick={() => onExport(doc)}
      withIconStart={<UIIcon src={DownloadLine} />}
    >
      Export
    </Button>
    <Button
      size="small"
      variant="outline"
      onClick={() => onCopySql(doc)}
      withIconStart={<UIIcon src={CopyLine} />}
    >
      {copiedDocumentId === doc.document_id ? 'Copied' : 'SQL'}
    </Button>
  </div>
);

const CompactDocumentList = ({
  docs,
  maxBroadcastsPerMinute,
  copiedDocumentId,
  copiedIdKey,
  onOpen,
  onRollback,
  onExport,
  onCopySql,
  onCopyId,
}: {
  docs: LivePlanningDocument[];
  maxBroadcastsPerMinute: number;
  copiedDocumentId: string | null;
  copiedIdKey: string | null;
  onOpen: (doc: LivePlanningDocument) => void;
  onRollback: (doc: LivePlanningDocument) => void;
  onExport: (doc: LivePlanningDocument) => void;
  onCopySql: (doc: LivePlanningDocument) => void;
  onCopyId: (value: string | number, key: string) => void;
}) => (
  <div className={styles.compactDocumentList}>
    {docs.map((doc, index) => (
      <section
        className={styles.compactDocumentCard}
        key={doc.document_id}
        style={{animationDelay: `${index * 55}ms`}}
      >
        <div className={styles.compactDocumentHeader}>
          <DocumentIdentity
            doc={doc}
            density="compact"
            copiedIdKey={copiedIdKey}
            onCopyId={onCopyId}
          />
          <div className={styles.compactStats}>
            <span className={styles.compactStat}>{doc.connected_clients} connections</span>
            <span className={styles.compactStat}>
              {doc.comment_counts.open_threads} open threads
            </span>
            <span className={styles.compactStat}>{doc.broadcasts_per_minute}/min</span>
          </div>
        </div>
        <div className={styles.compactSection}>
          <div className={styles.activityLabel}>Collaborators</div>
          <CollaboratorsCell collaborators={doc.collaborators} density="compact" />
        </div>
        <ActivityCell doc={doc} maxBroadcastsPerMinute={maxBroadcastsPerMinute} />
        <DocumentActions
          doc={doc}
          copiedDocumentId={copiedDocumentId}
          onOpen={onOpen}
          onRollback={onRollback}
          onExport={onExport}
          onCopySql={onCopySql}
          density="compact"
        />
      </section>
    ))}
  </div>
);

const LivePlanningTable = ({
  docs,
  density,
  maxBroadcastsPerMinute,
  copiedDocumentId,
  copiedIdKey,
  onOpen,
  onRollback,
  onExport,
  onCopySql,
  onCopyId,
}: {
  docs: LivePlanningDocument[];
  density: Exclude<LiveTableDensity, 'compact'>;
  maxBroadcastsPerMinute: number;
  copiedDocumentId: string | null;
  copiedIdKey: string | null;
  onOpen: (doc: LivePlanningDocument) => void;
  onRollback: (doc: LivePlanningDocument) => void;
  onExport: (doc: LivePlanningDocument) => void;
  onCopySql: (doc: LivePlanningDocument) => void;
  onCopyId: (value: string | number, key: string) => void;
}) => (
  <table className={styles.liveTable} data-density={density}>
    <colgroup>
      <col className={styles.documentColumn} />
      <col className={styles.collaboratorsColumn} />
      {density === 'full' ? (
        <>
          <col className={styles.metadataColumn} />
          <col className={styles.commentsColumn} />
          <col className={styles.broadcastsColumn} />
        </>
      ) : (
        <col className={styles.activityColumn} />
      )}
      <col className={styles.actionsColumn} />
    </colgroup>
    <thead>
      <tr>
        <th scope="col">Document</th>
        <th scope="col">Collaborators</th>
        {density === 'full' ? (
          <>
            <th scope="col">Live metadata</th>
            <th scope="col">Comments</th>
            <th scope="col">Broadcasts/min</th>
          </>
        ) : (
          <th scope="col">Activity</th>
        )}
        <th scope="col">Actions</th>
      </tr>
    </thead>
    <tbody>
      {docs.map((doc, index) => (
        <tr
          key={doc.document_id}
          className={styles.documentRow}
          style={{animationDelay: `${index * 55}ms`}}
        >
          <td>
            <DocumentIdentity
              doc={doc}
              density={density}
              copiedIdKey={copiedIdKey}
              onCopyId={onCopyId}
            />
          </td>
          <td>
            <CollaboratorsCell collaborators={doc.collaborators} density={density} />
          </td>
          {density === 'full' ? (
            <>
              <td>
                <MetadataBlock doc={doc} />
              </td>
              <td>
                <CommentsBlock doc={doc} />
              </td>
              <td>
                <BroadcastsBlock doc={doc} maxBroadcastsPerMinute={maxBroadcastsPerMinute} />
              </td>
            </>
          ) : (
            <td>
              <ActivityCell doc={doc} maxBroadcastsPerMinute={maxBroadcastsPerMinute} />
            </td>
          )}
          <td>
            <DocumentActions
              doc={doc}
              copiedDocumentId={copiedDocumentId}
              onOpen={onOpen}
              onRollback={onRollback}
              onExport={onExport}
              onCopySql={onCopySql}
              density={density}
            />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const AdminPlanningLiveContent = () => {
  const navigate = useNavigate();
  const [copiedDocumentId, setCopiedDocumentId] = useState<string | null>(null);
  const [copiedIdKey, setCopiedIdKey] = useState<string | null>(null);
  const [tableViewportRef, tableWidth] = useMeasuredElementWidth<HTMLDivElement>();

  const {data, isLoading, isFetching, error, refetch} = useQuery({
    queryKey: ['admin', 'planning-live'],
    queryFn: fetchLivePlanningAdmin,
    refetchInterval: 5_000,
  });

  const generatedAt = useMemo(() => {
    if (!data?.generated_at) return '—';
    return new Date(data.generated_at).toLocaleTimeString();
  }, [data?.generated_at]);
  const maxBroadcastsPerMinute = useMemo(
    () => Math.max(1, ...(data?.documents ?? []).map((doc) => doc.broadcasts_per_minute)),
    [data?.documents]
  );
  const tableDensity = getLiveTableDensity(tableWidth);

  const copySql = async (doc: LivePlanningDocument) => {
    await navigator.clipboard.writeText(sqlForDocument(doc));
    setCopiedDocumentId(doc.document_id);
    window.setTimeout(() => setCopiedDocumentId(null), 1500);
  };

  const copyId = async (value: string | number, key: string) => {
    await navigator.clipboard.writeText(String(value));
    setCopiedIdKey(key);
    window.setTimeout(() => setCopiedIdKey(null), 1500);
  };

  const openExport = (doc: LivePlanningDocument) => {
    window.location.href = `${getBackendUrl()}${doc.export_url}`;
  };

  const openPlanning = (doc: LivePlanningDocument) => {
    navigate(doc.planning_url);
  };

  const openRollback = (doc: LivePlanningDocument) => {
    navigate(doc.rollback_url);
  };

  const docs = data?.documents ?? [];
  const visibleConnectionCount = docs.reduce((sum, doc) => sum + doc.connected_clients, 0);
  const visibleCollaboratorCount = new Set(
    docs.flatMap((doc) => doc.collaborators.map((collaborator) => collaborator.user_id))
  ).size;
  const load = data?.server_load;

  return (
    <Stack gap="24">
      <Box display="flex" alignItems="center" justifyContent="space-between" style={{gap: 16}}>
        <Box>
          <div className={styles.updatedRow}>
            <Text size="small" color="faint">
              Updated {generatedAt}
            </Text>
            {isFetching ? (
              <Text size="small" color="faint">
                Refreshing
              </Text>
            ) : null}
          </div>
        </Box>
        <Button
          variant="outline"
          size="small"
          onClick={() => void refetch()}
          withIconStart={
            <UIIcon src={ClockLine} className={isFetching ? styles.refreshingIcon : undefined} />
          }
        >
          Refresh
        </Button>
      </Box>

      {data?.collab_error || error ? (
        <div className={styles.error}>
          <Text size="small">
            {data?.collab_error ?? (error instanceof Error ? error.message : 'Unable to load')}
          </Text>
        </div>
      ) : null}

      <div className={styles.summaryGrid}>
        <MetricPanel
          label="Active docs"
          value={formatNumber(docs.length, 0)}
          subtext={`${formatNumber(visibleConnectionCount, 0)} connections`}
          icon={DropboxPaperLine}
        />
        <MetricPanel
          label="Connected users"
          value={formatNumber(visibleCollaboratorCount, 0)}
          subtext={load?.uptime_formatted ? `uptime ${load.uptime_formatted}` : undefined}
          icon={PersonMultipleLine}
        />
        <MetricPanel
          label="Memory"
          value={`${formatNumber(load?.heap_used_mb)} MB`}
          subtext={`RSS ${formatNumber(load?.rss_mb)} MB`}
          icon={MetricsLine}
        />
        <MetricPanel
          label="WebSocket"
          value={
            load?.websocket_tracking_enabled === false
              ? 'Off'
              : `${formatNumber(load?.websocket_messages_per_second)} msg/s`
          }
          subtext={
            load?.websocket_tracking_enabled === false
              ? 'tracking disabled'
              : `${formatNumber(load?.websocket_bytes_per_second, 0)} B/s · ${formatNumber(
                  load?.websocket_messages_total,
                  0
                )} total`
          }
          icon={ActivityLine}
        />
        <MetricPanel
          label="Store"
          value={formatMs(load?.store_avg_time_ms)}
          subtext={`max ${formatMs(load?.store_max_time_ms)}`}
          icon={SyncingLine}
        />
        <MetricPanel
          label="Fetch"
          value={formatMs(load?.fetch_avg_time_ms)}
          subtext={`auth failures ${formatNumber(load?.auth_failures_recent, 0)}`}
          icon={CommentLine}
        />
      </div>

      {isLoading ? (
        <Box paddingY="24" display="flex" justifyContent="center">
          <Spinner size="large" />
        </Box>
      ) : docs.length === 0 ? (
        <div className={styles.emptyState}>
          <Text color="faint">No active planning documents.</Text>
        </div>
      ) : (
        <div className={styles.tableViewport} ref={tableViewportRef}>
          <div className={styles.tableScroll} data-density={tableDensity}>
            {tableDensity === 'compact' ? (
              <CompactDocumentList
                docs={docs}
                maxBroadcastsPerMinute={maxBroadcastsPerMinute}
                copiedDocumentId={copiedDocumentId}
                copiedIdKey={copiedIdKey}
                onOpen={openPlanning}
                onRollback={openRollback}
                onExport={openExport}
                onCopySql={(doc) => void copySql(doc)}
                onCopyId={(value, key) => void copyId(value, key)}
              />
            ) : (
              <LivePlanningTable
                docs={docs}
                density={tableDensity}
                maxBroadcastsPerMinute={maxBroadcastsPerMinute}
                copiedDocumentId={copiedDocumentId}
                copiedIdKey={copiedIdKey}
                onOpen={openPlanning}
                onRollback={openRollback}
                onExport={openExport}
                onCopySql={(doc) => void copySql(doc)}
                onCopyId={(value, key) => void copyId(value, key)}
              />
            )}
          </div>
        </div>
      )}
    </Stack>
  );
};
